import React, { FunctionComponent, useEffect, useState, useRef } from 'react'
import { Button, Form } from 'react-bootstrap'
import { ISelectOption, IForumThread, IForumTag } from 'Frontend/scripts/models/forums.model'
import {
  getThread,
  updateForumThread,
  unlockForumThread,
  lockforumthread,
  deleteForumThread,
  hasUserSubscribedToThread,
  removeSubscription,
  addSubscription,
} from 'Frontend/scripts/services/forums.service'
import ThreadAnswer from './ThreadAnswer'
import ThreadCreateAnswer from './ThreadCreateAnswer'
import MultiSelectComponent from 'Features/MultiSelectComponent/MultiSelectComponent'
import Dialog from 'Features/Dialog/Dialog'
import ReactQuill from 'react-quill-new'
import { getForumTags } from 'Frontend/scripts/services/forums.service'
import { convertDate, convertDateAndTime } from 'Frontend/scripts/convertDates'
import { quillModules, getSemanticHtml } from 'Frontend/scripts/quillModules'
import { IAntiforgeryToken } from 'Frontend/scripts/models/general.model'
import { getAntiforgeryToken } from 'Frontend/scripts/services/general.service'
import { useForm, Controller } from 'react-hook-form'
import { IHasSubscription } from 'Frontend/scripts/models/subscription.model'
import { useTranslation } from 'react-i18next'

interface ThreadProps {
  listingpageUrl: string | null
}

interface EditThreadForm {
  heading: string
  mainbody: string
  tags: ISelectOption[]
}

const Thread: FunctionComponent<ThreadProps> = ({ listingpageUrl }) => {
  const queryString = window.location.search
  const urlParams = new URLSearchParams(queryString)
  const forumThreadId = urlParams.get('id')

  const [threadData, setThreadData] = useState<IForumThread>()
  const [isUpdated, setIsUpdated] = useState<boolean>(false)
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [validated, setValidated] = useState(false)
  const [mainbody, setMainbody] = useState<string>('')
  const [heading, setHeading] = useState<string>('')
  const [tags, setTags] = useState<ISelectOption[]>([])
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)
  const [isLocked, setIsLocked] = useState<boolean>(false)
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [displayTags, setDisplayTags] = useState<ISelectOption[]>([])
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false)
  const [isSubscribing, setIsSubscribing] = useState<boolean>(false)
  const options = [] as ISelectOption[]
  const quillRef = useRef<ReactQuill | null>(null)
  const { t } = useTranslation()

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm<EditThreadForm>({
    defaultValues: {
      heading: '',
      mainbody: '',
      tags: [],
    },
  })

  useEffect(() => {
    getThread(forumThreadId as string).then((data: IForumThread) => {
      const threadsTags = [] as ISelectOption[]
      setThreadData(data)
      setMainbody(data.thread.text)
      setHeading(data.thread.title)
      setIsLocked(data.thread.isLocked)

      const options = [] as ISelectOption[]
      data.thread.tags.map((tag) => {
        options.push({ value: tag.tagId.toString(), label: tag.name })
      })
      setDisplayTags(options)
      data.thread.tags.map((tag) => {
        threadsTags.push({ value: tag.tagId.toString(), label: tag.name })
      })
    })
  }, [isUpdated])

  const createMarkup = (body: string) => {
    return { __html: body }
  }

  const abortEditing = () => {
    setMainbody(threadData?.thread.text as string)
    setIsEditing(false)
  }

  const submitEditedThread = (data: EditThreadForm) => {
    const selectedTagsValueList = data.tags?.map((item: ISelectOption) => item.value.toString())
    const mainBodyWithCorrectHTML = getSemanticHtml(quillRef)
    const matchingObjects = tags.filter((obj) => selectedTags.includes(obj.value))

    setDisplayTags(matchingObjects)
    getAntiforgeryToken().then((token: IAntiforgeryToken) => {
      updateForumThread(
        token.csrfToken,
        data.heading,
        mainBodyWithCorrectHTML,
        selectedTagsValueList,
        parseInt(forumThreadId as string),
      ).then(() => {
        setIsEditing(false)
        setMainbody(mainBodyWithCorrectHTML)
        setHeading(data.heading)
      })
    })
    setValidated(true)
  }

  const toggleDialog = () => {
    setIsDialogOpen(true)
  }

  const updateForumThreadToUnlocked = () => {
    getAntiforgeryToken().then((token: IAntiforgeryToken) => {
      unlockForumThread(token.csrfToken, threadData?.thread.id as number).then(() => {
        setIsLocked(false)
        setIsDialogOpen(false)
      })
    })
  }

  const updateForumThreadToLocked = () => {
    getAntiforgeryToken().then((token: IAntiforgeryToken) => {
      lockforumthread(token.csrfToken, threadData?.thread.id as number).then(() => {
        setIsLocked(true)
        setIsDialogOpen(false)
      })
    })
  }

  const deleteThread = () => {
    getAntiforgeryToken().then((token: IAntiforgeryToken) => {
      deleteForumThread(token.csrfToken, threadData?.thread.id as number).then(() => {
        if (listingpageUrl != null) {
          window.location.href = listingpageUrl
        }
      })
    })
  }

  const updateSubscription = (checked: boolean) => {
    getAntiforgeryToken().then((token: IAntiforgeryToken) => {
      if (checked) {
        addSubscription(token.csrfToken, parseInt(forumThreadId as string))
      } else {
        removeSubscription(token.csrfToken, forumThreadId as string)
      }
    })
  }

  useEffect(() => {
    if (threadData) {
      reset({
        heading: threadData.thread.title || '',
        mainbody: threadData.thread.text || '',
        tags:
          threadData.thread.tags.map((tag) => {
            return { value: tag.tagId.toString(), label: tag.name }
          }) || {},
      })
    }
  }, [threadData, reset])

  useEffect(() => {
    getForumTags().then((data: IForumTag[]) => {
      options.length = 0
      data.map((tag) => {
        options.push({ value: tag.id.toString(), label: tag.name })
      })
      setTags(options)
    })
  }, [])

  useEffect(() => {
    hasUserSubscribedToThread(forumThreadId as string).then((subscription: IHasSubscription) => {
      setIsSubscribing(subscription.hasSubscription)
    })
  }, [isUpdated])

  return (
    <>
      <div className="livstecknet__colored-area mb-4">
        {!isEditing ? (
          <>
            <div className="thread__subscribe mb-2">
              <Form.Check
                type="switch"
                id="custom-switch"
                label={t('subscribe-on-updates')}
                checked={isSubscribing}
                onChange={(e) => {
                  updateSubscription(e.target.checked)
                  setIsSubscribing(e.target.checked)
                }}
              />
            </div>
            <h1 className="mb-3 me-2 text-break">{heading}</h1>
            {displayTags && displayTags?.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mb-3">
                {displayTags.map((tag) => (
                  <div
                    className="bg-secondary text-white rounded-pill forum-thread__tags text-capitalize"
                    key={`tag-${tag.value}`}
                  >
                    {tag.label}
                  </div>
                ))}
              </div>
            )}
            {mainbody && <div className="mb-3" dangerouslySetInnerHTML={createMarkup(mainbody)}></div>}

            <div className="d-flex gap-2">
              {threadData?.thread.createdByUser == null ? (
                <div className="livstecknet-thread__profile-initial-letters">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                    <use xlinkHref="#icon-profile" />
                  </svg>
                </div>
              ) : threadData.thread.createdByUser.profileImageUrl == null ? (
                <div className="livstecknet-thread__profile-initial-letters">
                  {threadData?.thread.createdByUser?.firstname?.charAt(0)}
                  {threadData?.thread.createdByUser?.lastname?.charAt(0)}
                </div>
              ) : (
                <img
                  className="workrooms__profile-image"
                  src={threadData.thread.createdByUser.profileImageUrl}
                  alt={`${t('image-of')} ${threadData?.thread.createdByUser?.firstname} ${threadData?.thread.createdByUser?.lastname}`}
                />
              )}

              <div className="align-self-center">
                {threadData?.thread.createdByUser != null && (
                  <a href={threadData.thread.createdByUser.viewProfileUrl} className="livstecknet-thread__author">
                    {threadData?.thread.createdByUser?.firstname}&nbsp;
                    {threadData?.thread.createdByUser?.lastname}
                  </a>
                )}
                {threadData?.thread.created && (
                  <p className="livstecknet-thread__time mb-0">{convertDateAndTime(threadData?.thread.created)}</p>
                )}
              </div>
            </div>
            {threadData?.thread.lastReviewed && (
              <p className="livstecknet-thread__time mb-0 mt-3">
                {t('thread.latest-reviewed')}: {convertDate(threadData?.thread.lastReviewed)}
              </p>
            )}
            <div className="d-flex flex-shrink-0 gap-2 justify-content-end livstecknet-thread__handle-thread">
              {threadData?.thread.hasCurrentUserAccessToUpdate && !isLocked && (
                <Button
                  variant="outline-secondary"
                  className="gap-2 livstecknet-thread__edit-question"
                  onClick={() => setIsEditing(true)}
                >
                  <span className="fa fa-pencil"></span>
                  {t('edit')}
                </Button>
              )}
              {threadData?.thread.hasCurrentUserAccessToDelete && (
                <Button
                  variant="dark"
                  className="gap-2 livstecknet-thread__edit-question"
                  onClick={() => setIsDeleteDialogOpen(true)}
                >
                  <span className="fa fa-trash-o"></span>
                  {t('delete')}
                </Button>
              )}
              {isLocked && threadData?.thread.hasCurrentUserAccessToUnlock ? (
                <Button
                  variant="secondary"
                  className="gap-2 livstecknet-thread__edit-question"
                  onClick={() => toggleDialog()}
                >
                  <span className="fa fa-key"></span>
                  {t('open-question')}
                </Button>
              ) : threadData?.thread.hasCurrentUserAccessToLock ? (
                <Button
                  variant="dark"
                  className="gap-2 livstecknet-thread__edit-question"
                  onClick={() => toggleDialog()}
                >
                  <span className="fa fa-key"></span>
                  {t('close-question')}
                </Button>
              ) : null}
            </div>
            {isLocked ? (
              <Dialog
                isOpen={isDialogOpen}
                verificationText={t('verification-open-question')}
                submitText={t('yes-open-question')}
                abortText={t('abort')}
                buttonVariant="secondary"
                callbackSubmit={() => updateForumThreadToUnlocked()}
                callbackClose={() => setIsDialogOpen(false)}
              />
            ) : (
              <Dialog
                isOpen={isDialogOpen}
                verificationText={t('verification-close-question')}
                submitText={t('yes-close-question')}
                abortText={t('abort')}
                buttonVariant="dark"
                callbackSubmit={() => updateForumThreadToLocked()}
                callbackClose={() => setIsDialogOpen(false)}
              />
            )}
            <Dialog
              isOpen={isDeleteDialogOpen}
              verificationText={t('verification-delete-thread')}
              submitText={t('yes-delete-thread')}
              abortText={t('abort')}
              buttonVariant="dark"
              callbackSubmit={() => deleteThread()}
              callbackClose={() => setIsDeleteDialogOpen(false)}
            />
          </>
        ) : (
          <>
            <Form
              noValidate
              validated={validated}
              onSubmit={handleSubmit(submitEditedThread)}
              className="d-flex flex-column gap-3"
            >
              <Form.Group>
                <Form.Label htmlFor="inputThreadHeading">{t('heading')}</Form.Label>
                <Form.Control
                  required
                  type="text"
                  id="inputThreadHeading"
                  {...register('heading', { required: t('error-add-heading') })}
                />
                {errors.heading && (
                  <Form.Text className="text-danger invalid-feedback d-block">{errors.heading.message}</Form.Text>
                )}
              </Form.Group>
              <Form.Group>
                <Form.Label htmlFor="inputThreadMainBody">{t('text')}</Form.Label>
                <Controller
                  name="mainbody"
                  control={control}
                  rules={{
                    validate: (value) => value?.replace(/<(.|\n)*?>/g, '').trim().length > 0 || t('error-text'),
                  }}
                  render={({ field }) => (
                    <ReactQuill
                      {...field}
                      ref={quillRef}
                      id="inputThreadMainBody"
                      className="notice__quill-editor"
                      theme="snow"
                      modules={quillModules}
                      onChange={(value) => setValue('mainbody', value, { shouldValidate: true })}
                    />
                  )}
                />
                {errors.mainbody && (
                  <Form.Text className="text-danger invalid-feedback d-block">{errors.mainbody.message}</Form.Text>
                )}
              </Form.Group>
              <Form.Group>
                <Form.Label htmlFor="tags">{t('question-is-about')}</Form.Label>
                <Controller
                  name="tags"
                  control={control}
                  render={({ field }) => (
                    <MultiSelectComponent
                      inputId="tags"
                      options={tags}
                      addToUrl={false}
                      callback={(selectedTagIds) => {
                        setSelectedTags(selectedTagIds)
                      }}
                      value={field.value || []}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Form.Group>
              <div className="d-flex gap-3">
                <Button variant="secondary" type="submit">
                  {t('save-and-publish')}
                </Button>
                <Button variant="outline-secondary" type="button" onClick={() => abortEditing()}>
                  {t('abort')}
                </Button>
              </div>
            </Form>
          </>
        )}
      </div>

      <h2 className="livstecknet__section-headline-text">{t('answers')}</h2>
      <div className="d-flex flex-column gap-3">
        {threadData?.posts?.map((post) => (
          <ThreadAnswer
            key={`key-post-${post.forumPostId}`}
            post={post}
            forumThreadId={threadData.thread.id}
            callback={() => setIsUpdated(!isUpdated)}
          />
        ))}
      </div>
      {!isLocked && threadData?.thread.id && (
        <ThreadCreateAnswer forumThreadId={threadData.thread.id} callback={() => setIsUpdated(!isUpdated)} />
      )}
    </>
  )
}
export default Thread
